import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  Flag,
  FlagGroup,
  FlagStatus,
  Inspection,
  InspectionItem,
  ItemInfo,
  Setting,
  UnderwritingStatus,
} from '../../shared/interfaces';
import { InspectionCommentComponent } from '../inspection-comment/inspection-comment.component';
import { InspectionFilters, ReviewNavigationComponent } from '../review.navigation/review.navigation.component';
import { FlagGroupService } from '../../flag/flag.group.service';
import { FlagService } from '../../flag/flag.service';
import { ActivatedRoute, Router } from '@angular/router';
import { VersionService } from '../../version/version.service';
import { InspectionService } from '../inspection.service';
import { FLAG_MANAGER_STATUSES, StatusOption, UnlinkAllOption } from './flag-manager.interfaces';
import { InspectionCommentsComponent } from './components/inspection-comments/inspection-comments.component';
import { env } from '../../../environments/environment';
import { UntypedFormControl } from '@angular/forms';
import { FlagManagerEditGroupComponent } from './components/flag-manager-edit-group/flag-manager-edit-group.component';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { FlagGroupTag } from '../../shared/interfaces';
import { FlagGroupTemplateComponent } from '../../shared/_components/flag-group-template/flag-group-template.component';
import { FlagManagerService } from './flag-manager.service';
import { FlagUnlinkConfirmModalComponent } from './components/flag-unlink-confirm-modal/flag-unlink-confirm-modal.component';
import { GroupNameModalComponent } from './components/group-name-modal/group-name-modal.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-flag-manager-new-design',
  templateUrl: './flag-manager.component.html',
  styleUrls: ['./flag-manager.component.scss'],
})
export class FlagManagerNewDesignComponent implements OnInit, OnChanges, OnDestroy {
  readonly statuses: StatusOption[] = FLAG_MANAGER_STATUSES;
  constructor(
    private flagService: FlagService,
    private groupService: FlagGroupService,
    public route: ActivatedRoute,
    private versionService: VersionService,
    private inspectionService: InspectionService,
    private router: Router,
    private toastr: ToastrService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private flagGroupService: FlagGroupService,
    private dialog: DialogService,
    private flagManagerService: FlagManagerService,
  ) {}

  private subscriptions: Subscription[] = [];

  get selectedGroup() {
    return this._selectedGroup;
  }
  set selectedGroup(x) {
    if (this._selectedGroup !== x) {
      this._selectedGroup = x;
      this.setCurrentFlags();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { groupId: this.selectedGroup ? x.id : null, flagId: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  get underwritingStatus() {
    return this._underwritingStatus;
  }
  set underwritingStatus(x) {
    this._underwritingStatus = x;
    this.inspection.underwritingStatus = +this.underwritingStatus;
    this.calculateTabsCount();
    this.selectedTab = this.getInitialSelectedTab();
    this.flagManagerService.deselectAllFlags();
  }

  @Input() inspection: Inspection;
  @Input() filters: InspectionFilters;
  @Input() navigation: ReviewNavigationComponent;
  @Input() existingLetters: any[];
  @Input() answers;
  // Remove @Input() - this is calculated locally
  tabsCount = { included: 0, toResolve: 0, resolved: 0, excluded: 0 };

  @Output() refreshAnswers = new EventEmitter<void>();
  @ViewChild(InspectionCommentComponent) inspectionCommentComponent: InspectionCommentComponent;
  @ViewChild('commentsModal', { static: false }) commentsModal!: InspectionCommentsComponent;
  @ViewChild('editGroupModal', { static: false }) editGroupModal!: FlagManagerEditGroupComponent;
  @ViewChild('flagGroupTemplateModal', { static: false }) flagGroupTemplateModal!: FlagGroupTemplateComponent;
  @ViewChild('unlinkConfirm', { static: false }) unlinkConfirm!: FlagUnlinkConfirmModalComponent;
  @ViewChild('sidebarGroupNameModal', { static: true }) sidebarGroupNameModal!: GroupNameModalComponent;

  _underwritingStatus: UnderwritingStatus;

  groups: any[];
  items: any[];
  allFlags: any[];
  _selectedGroup: FlagGroup;
  subtypeInfo: Setting[];
  countUncategorized = 0;
  allCommentsLength;
  allQuestions = [];
  editingFlag;
  currentFlags = [];
  issueNo: number = 0;
  editingGroup;
  selectedFlagIds: number[] = [];
  managingGroups: boolean = false;
  selectedFlags;
  flagGroupTags: FlagGroupTag[] = [];
  shouldShowResolveOptions: boolean = false;
  showLetterHistoryPanel: boolean = false;
  selectedTab: string;
  resolveBarSelectedTab: string;
  loading = false;
  isIncludedGroup;
  includedGroups: any[] = [];
  includedGroupsFlags: any[] = [];
  editGroup = false;
  canCreateGroup = true;
  grouploading = true;

  get ignored() {
    if (!this.selectedGroup) {
      return this.currentFlags;
    }
    return this.currentFlags.filter(f => f.ignore);
  }

  get included() {
    if (!this.selectedGroup) {
      return [];
    }
    return this.currentFlags.filter(f => !f.ignore);
  }

  async ngOnInit() {
    this.flagGroupTags = await this.flagGroupService.tags();
    for (const group of this.flagGroupTags) {
      if (group.subgroups && group.subgroups.length > 0) {
        for (const subgroup of group.subgroups) {
          const templates = await this.flagGroupService.templates(subgroup.id);
          subgroup.templates = templates;
        }
      }
    }

    const flagOrderSub = this.flagManagerService.getFlagOrderUpdated().subscribe(orderUpdate => {
      if (orderUpdate) {
        this.updateFlagOrderInAllFlags(orderUpdate.flagId, orderUpdate.newOrder);
      }
    });

    const statusChangeSub = this.flagManagerService.getStatusChange().subscribe(statusChange => {
      if (statusChange && statusChange.inspectionId === this.inspection?.id) {
        this.underwritingStatus = statusChange.statusId;
        this.cdr.detectChanges();
      }
    });

    const includedGroupsSub = this.flagManagerService.getIncludedGroups().subscribe(groups => {
      if (!this.allFlags || this.allFlags.length === 0) {
        console.warn('allFlags is empty, skipping includedGroupsFlags update');
        this.includedGroups = groups;
        this.grouploading = false;
        return;
      }

      const includedGroupsFlags = groups
        .map(group => group.flags || []) // Handle null/undefined flags
        .flat();
      const includedFlagIds = includedGroupsFlags.map(flag => flag.id);
      this.includedGroupsFlags = this.allFlags.filter(flag => includedFlagIds.includes(flag.id));

      this.includedGroups = groups;
      this.grouploading = false;
    });

    const pictureUpdateSub = this.flagManagerService.getFlagPictureUpdates().subscribe(picture => {
      if (picture) {
        // update all flags across all groups
        this.updateFlagsWithPicture(this.allFlags, picture);
        // update current flags array
        this.updateFlagsWithPicture(this.currentFlags, picture);
      }
    });

    this.subscriptions.push(flagOrderSub, statusChangeSub, includedGroupsSub, pictureUpdateSub);
  }

  ngDoCheck() {
    const timestamp = Date.now();

    this.currentFlags?.forEach(flag => {
      const pictureId = flag?.picture?.id;
      if (!pictureId) return;

      if (!flag.imageUrl || flag._needsImageRefresh) {
        flag.imageUrl = `${env.APIUrl}/image/embedded/${pictureId}?t=${timestamp}`;
        flag._needsImageRefresh = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('answers' in changes) {
      if (this.answers) {
        this.load();
      }
    }
    if ('filters' in changes) {
      this.setCurrentFlags();
    }

    if ('inspection' in changes) {
      this._underwritingStatus = this.inspection && this.inspection.underwritingStatus;
      // Set default tab based on underwriting status
      this.selectedTab = this.getInitialSelectedTab();
    }
  }

  async load() {
    if (!this.inspection) return;
    this.loading = true;
    const selectedGroupId = this.selectedGroup?.id;
    try {
      this.subtypeInfo = await this.flagService.subtypeInfo;
      await this.reloadAnswers();
      await this.loadGroups();
      await this.loadItems();
      await this.mergeAnswers();
      await this.loadComments();
      await this.getAllFlags();
      await this.calculateTabsCount;

      const params = this.route.snapshot.queryParams;
      if (params.groupId) {
        const group = this.groups.find(g => g.id === Number(params.groupId));
        if (!this.selectedGroup) this.openGroup(group);
      } else if (params.flagId) {
        this.editingFlag = this.allFlags.find(f => f.id === Number(params.flagId));
      }
      this.selectedFlagIds = [];
      if (selectedGroupId) {
        const refreshedGroup = this.groups.find(g => g.id === selectedGroupId);
        this.selectedGroup = refreshedGroup || null;
      }
    } catch (error) {
      console.error('Error loading flag manager data:', error);
    } finally {
      this.loading = false;
    }
  }

  getComments(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => [...acc, ...cur.comments], []);
      } else {
        return q.answer.comments;
      }
    }
    return [];
  }

  loadComments() {
    this.allQuestions = [];

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        const sectionComments = [];

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          const comments = this.getComments(question);

          sectionComments.push(
            ...comments.map(f => ({
              ...f,
              questionId: question.id,
              question: question.text,
            })),
          );
        }

        if (sectionComments.length) {
          this.allQuestions.push(...sectionComments);
        }
      }
    }
  }

  async mergeAnswers() {
    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;
      const buildingIndex = item.index;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];

          if (question.repeat && question.repeat.length) {
            question.repeat.forEach(itemId => {
              question.answer = this.answers.filter(ans => {
                return ans.questionId === question.id;
              });
            });
          } else {
            const answer = this.answers.find(ans => {
              const indexOk = !buildingIndex || ans.buildingIndex === buildingIndex;
              return ans.questionId === question.id && indexOk;
            });

            if (answer) {
              question.answer = answer;
            }
          }

          const answers = Array.isArray(question.answer) ? question.answer : [question.answer];

          answers.forEach(answer => {
            if (answer) {
              answer.question = question;

              // set images' path for tooltip
              answer.pictures.forEach(pic => {
                pic.path = `${item.label} / ${section.name} / ${question.text}`;
              });

              // set flags & comments' deeplink
              [...answer.flags, ...answer.comments].forEach((x: any) => {
                x.deeplink = [+iItem, +iSection, +iQuestion];
              });
            }
          });
        }
      }
    }
  }

  async loadGroups() {
    this.groups = (await this.groupService.listByInspection(this.inspection.id))[0];
    this.groups.sort((a, b) => a.order - b.order);

    this.groups.forEach(group => {
      group.checkbox = new UntypedFormControl(false);
    });
  }

  async reloadAnswers() {
    this.answers = await this.inspectionService.answers(this.inspection.id);
  }

  async loadItems() {
    this.items = [];

    const items = (await this.versionService.tree(this.inspection.versionId)).items;

    // filter empty sections
    items.forEach(item => {
      item.sections = item.sections.filter(section => section.questions.length);
    });

    // filter empty items
    items.forEach((item: any, i) => {
      if (!item.sections.length) {
        delete items[i];
      } else {
        item.info = this.inspection.items.find(info => info.itemId === item.id);
      }
    });

    items.forEach((item: InspectionItem & { info: ItemInfo }) => {
      if (!item.info) {
        return;
      }

      if (item.info.quantity === 1) {
        const customName = item.info.names && item.info.names[0];
        const defaultName = item.name;
        const label = customName ? `${customName} (${defaultName})` : defaultName;

        this.items.push({
          ...item,
          label,
        });
      } else if (item.info.quantity > 1) {
        for (let i = 0; i < item.info.quantity; i++) {
          const customName = item.info.names && item.info.names[i];
          const defaultName = item.name;
          const label = customName ? `${customName} (${defaultName})` : defaultName;

          this.items.push({
            // deep copy
            ...JSON.parse(JSON.stringify(item)),
            label,
            index: i + 1,
          });
        }
      }
    });
  }

  async getAllFlags() {
    this.allFlags = [];

    for (const iItem in this.items) {
      const item = this.items[iItem];
      const sections = item.sections;
      const buildingIndex = item.index;

      for (const iSection in sections) {
        const section = sections[iSection];
        const questions = section.questions;

        const sectionFlags = [];

        for (const iQuestion in questions) {
          const question = questions[iQuestion];
          const flags = this.getFlags(question);

          sectionFlags.push(
            ...flags.map(f => ({
              ...f,
              flag: f,
              questionId: question.id,
              question: `${question.legacyId ? question.legacyId + '.' : ''} ${question.text}`,
              label: `${item.label} ${buildingIndex ? '#' + buildingIndex : ''} / ${section.name}`,
              hasPictures: this.nPictures(question),
              deeplink: f.deeplink || [+iItem, +iSection, +iQuestion],
            })),
          );
        }

        if (sectionFlags.length) {
          this.allFlags.push(...sectionFlags);
        }
      }
    }
    await this.setCurrentFlags();
  }

  nPictures(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, cur) => acc + cur.pictures.filter(p => !p.related).length, 0);
      } else {
        return q.answer.pictures.filter(p => !p.related).length;
      }
    }
    return 0;
  }

  getFlags(q) {
    if (q?.answer) {
      if (Array.isArray(q.answer)) {
        return q.answer.reduce((acc, ans) => [...acc, ...ans.flags.filter(f => f.answerId === ans.id)], []);
      } else {
        return q.answer.flags.filter(f => f.answerId === q.answer.id);
      }
    }
    return [];
  }

  async setCurrentFlags() {
    if (!this.allFlags) {
      return;
    }

    const group = this.selectedGroup;
    let filteredFlags = this.allFlags.filter(f =>
      this.filters && this.filters.questions === 'autoScored' ? f.systemFlag : true,
    );

    if (this.filters && this.filters.flags && this.filters.flags !== 'all') {
      filteredFlags = this.allFlags.filter(
        f => this.filters && this.filters.flags !== 'all' && this.filters.flags.includes(f.subtypeId),
      );
    }

    if (this.filters && this.filters.withDeadlineExtension) {
      filteredFlags = this.allFlags.filter(f => this.hasExtendedDeadline(f));
    }

    this.currentFlags = filteredFlags
      .filter(f => f.groupId == group?.id)
      .map(flag => ({
        ...flag,
        changes: flag.changes ? [...flag.changes] : flag.changes,
        flag: flag.flag
          ? {
              ...flag.flag,
              changes: flag.flag.changes ? [...flag.flag.changes] : flag.flag.changes,
            }
          : flag.flag,
      }))
      .sort((a, b) => {
        const ao = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
        const bo = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return a.id - b.id;
      });
    this.groups.forEach(g => (g.count = filteredFlags.filter(f => f.groupId === g.id).length));
    this.countUncategorized = filteredFlags.filter(f => !f.groupId).length;

    setTimeout(() => {
      const groupsWithFlags = this.groups.map(g => {
        const groupFlags = filteredFlags.filter(flag => flag.groupId === g.id);
        return {
          ...g,
          flags: groupFlags,
        };
      });

      const extendedDeadlineFilter = !!this.filters?.withDeadlineExtension;
      const allFlagsFilter = !!(this.filters && !this.filters.withoutFlags && this.filters.flags !== 'all');
      const autoScoredFilter = !!(this.filters?.withoutFlags && this.filters?.flags === 'all');

      if (extendedDeadlineFilter || allFlagsFilter || autoScoredFilter) {
        const groupsToSeparate = groupsWithFlags.filter(g => g.count > 0);
        this.flagManagerService.separateGroups(groupsToSeparate);
        if (this.selectedGroup !== null) {
          const currentGroupHasFlags = filteredFlags.some(f => f.groupId === this.selectedGroup.id);
          if (!currentGroupHasFlags) {
            const firstGroupWithFlags = groupsWithFlags?.find(g => filteredFlags.some(f => f.groupId === g.id));
            this.openGroup(firstGroupWithFlags ?? null);
          }
        }
      } else {
        this.flagManagerService.separateGroups(groupsWithFlags);
      }
    }, 0);

    this.isIncludedGroup = this.allSelectedAreIncluded();
    this.calculateTabsCount();
    this.selectedTab = this.getInitialSelectedTab();
  }

  updateFlagOrderInAllFlags(flagId: number, newOrder: number): void {
    // Update order in allFlags
    const flag = this.allFlags.find(f => f.id === flagId);
    if (flag) {
      flag.order = newOrder;
      if (flag.flag) {
        flag.flag.order = newOrder;
      }
    }

    // Update order in currentFlags
    const currentFlag = this.currentFlags.find(f => f.id === flagId);
    if (currentFlag) {
      currentFlag.order = newOrder;
      if (currentFlag.flag) {
        currentFlag.flag.order = newOrder;
      }
    }

    // Update order in group flags
    if (flag?.groupId) {
      const group = this.groups.find(g => g.id === flag.groupId);
      if (group?.flags) {
        const groupFlag = group.flags.find(f => f.id === flagId);
        if (groupFlag) {
          groupFlag.order = newOrder;
        }
      }
    }

    // Refresh currentFlags to trigger change detection
    this.refreshCurrentFlags();
  }

  refreshCurrentFlags(): void {
    const group = this.selectedGroup;
    let filteredFlags = this.allFlags.filter(f =>
      this.filters && this.filters.questions === 'autoScored' ? f.systemFlag : true,
    );

    if (this.filters && this.filters.flags && this.filters.flags !== 'all') {
      filteredFlags = this.allFlags.filter(
        f => this.filters && this.filters.flags !== 'all' && this.filters.flags.includes(f.subtypeId),
      );
    }

    if (this.filters && this.filters.withDeadlineExtension) {
      filteredFlags = filteredFlags.filter(f => this.hasExtendedDeadline(f));
    }

    this.currentFlags = filteredFlags
      .filter(f => f.groupId == group?.id)
      .map(flag => ({
        ...flag,
        changes: flag.changes ? [...flag.changes] : flag.changes,
        flag: flag.flag
          ? {
              ...flag.flag,
              changes: flag.flag.changes ? [...flag.flag.changes] : flag.flag.changes,
            }
          : flag.flag,
      }))
      .sort((a, b) => {
        const ao = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
        const bo = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
        if (ao !== bo) return ao - bo;
        return a.id - b.id;
      });
  }

  async refreshFlagsWithChanges(flagIds: number[]): Promise<void> {
    try {
      const refreshPromises = flagIds.map(async flagId => {
        try {
          const updatedFlag = await this.flagService.get(flagId);
          const flag = this.allFlags.find(f => f.id === flagId);
          if (flag) {
            flag.changes = updatedFlag.changes;
            if (flag.flag) {
              flag.flag.changes = updatedFlag.changes;
            }
          }
        } catch (error) {
          console.error(`Error refreshing flag ${flagId}:`, error);
        }
      });
      await Promise.all(refreshPromises);

      this.allFlags = this.allFlags.map(flag => ({
        ...flag,
        changes: flag.changes ? [...flag.changes] : flag.changes,
        flag: flag.flag
          ? {
              ...flag.flag,
              changes: flag.flag.changes ? [...flag.flag.changes] : flag.flag.changes,
            }
          : flag.flag,
      }));
    } catch (error) {
      console.error('Error refreshing flags with changes:', error);
    }
  }

  async refreshFlagsAndNotifyUpdates(
    flagIds: number[] | null = null,
    options: { reload?: boolean; refreshAllInGroup?: boolean } = {},
  ): Promise<void> {
    const { reload = false, refreshAllInGroup = false } = options;

    try {
      if (reload) {
        await this.load();
      }

      let flagsToRefresh: number[] = [];
      if (refreshAllInGroup && this.selectedGroup) {
        const targetGroupFlags = this.allFlags.filter(f => f.groupId === this.selectedGroup.id);
        flagsToRefresh = targetGroupFlags.map(f => f.id);
      } else if (flagIds && flagIds.length > 0) {
        flagsToRefresh = flagIds;
      }

      if (flagsToRefresh.length > 0) {
        await this.refreshFlagsWithChanges(flagsToRefresh);
      }

      this.refreshCurrentFlags();
      this.flagManagerService.notifyFlagsUpdated();
    } catch (error) {
      console.error('Error refreshing flags and notifying updates:', error);
    }
  }

  async onFlagStatusChanged(event: { flagId: number; status: number }) {
    const flag = this.allFlags.find(f => f.id === event.flagId);
    if (flag) {
      flag.status = event.status;
      if (flag.flag) {
        flag.flag.status = event.status;
      }
    }
    this.allFlags = [...this.allFlags];

    const currentFlag = this.currentFlags.find(f => f.id === event.flagId);
    if (currentFlag) {
      currentFlag.status = event.status;
      if (currentFlag.flag) {
        flag.flag.status = event.status;
      }

      this.currentFlags = [...this.currentFlags];
    }
    this.calculateTabsCount();
    this.checkAndSwitchToResolvedTab();

    await this.refreshFlagsAndNotifyUpdates([event.flagId]);
    if (this.tabsCount.toResolve === 0 && this.tabsCount.resolved > 0 && this.selectedTab === 'resolved') {
      this.toastr.success('You have successfully resolved flags and moved to the Resolved Flags tab.');
    } else {
      this.toastr.success('Flag saved');
    }

    if (
      this.underwritingStatus != UnderwritingStatus.RESOLUTION_IN_PROGRESS &&
      this.underwritingStatus != UnderwritingStatus.CLOSED
    ) {
      this.flagManagerService.updateUnderwritingStatus(this.inspection.id, UnderwritingStatus.RESOLUTION_IN_PROGRESS);
    }
  }
  //for real time update
  async onFlagIgnored(flag: any) {
    const groupFlagsBefore = Array.isArray(this.currentFlags) ? this.currentFlags : [];
    const wasAnyIncluded = groupFlagsBefore.some(f => !f.ignore);
    const wasAllExcluded = groupFlagsBefore.length > 0 && groupFlagsBefore.every(f => f.ignore);

    const selectedIds = new Set([flag.id]);
    this.updateFlagIgnoreState(selectedIds, flag.ignore);
    this.setCurrentFlags();

    await this.refreshFlagsAndNotifyUpdates([flag.id]);

    const groupId = this.selectedGroup?.id;
    const groupFlagsNow = (this.allFlags || []).filter(f => f.groupId === groupId);
    const isNowAllExcluded = groupFlagsNow.length > 0 && groupFlagsNow.every(f => f.ignore);
    const isNowAnyIncluded = groupFlagsNow.some(f => !f.ignore);
    this.refreshAnswers.emit();

    if (wasAnyIncluded && isNowAllExcluded) {
      this.toastr.success('All flags excluded; issue moved to Not on Rec Letter and flags moved to Excluded tab.');
      return;
    }
    if (!wasAnyIncluded && isNowAnyIncluded) {
      this.toastr.success('Flags included; issue moved to Included on Rec Letter and flags moved to Included tab.');
      return;
    }

    if (flag.ignore) {
      this.toastr.success('Flag excluded and moved to the Excluded tab.');
    } else {
      this.toastr.success('Flag included and moved to the Included tab.');
    }
  }

  reSeparateGroups(group: any): void {
    if (this.groups && this.allFlags) {
      // Update the this.groups with the new group
      this.groups = this.groups.map(g => (g.id === group.id ? group : g));

      const groupsWithFlags = this.groups.map(g => {
        const groupFlags = this.allFlags.filter(flag => flag.groupId === g.id);
        return {
          ...g,
          flags: groupFlags.length > 0 ? groupFlags : g.flags || [],
        };
      });

      this.flagManagerService.separateGroups(groupsWithFlags);
    }
  }

  async onFlagReordered(): Promise<void> {
    // Reload all flags from server to get correct order values
    await this.load();
  }

  async onDropReverted(): Promise<void> {
    // Reload to resync Sortable.js state after a blocked drop
    await this.load();
  }

  private calculateTabsCount(): void {
    this.tabsCount = {
      included: this.included.length,

      toResolve:
        this.underwritingStatus >= UnderwritingStatus.LETTER_SENT
          ? this.currentFlags.filter(
              f =>
                (f.status === FlagStatus.UNRESOLVED || f.status === FlagStatus.PARTIAL_RESPONSE) &&
                !f.ignore &&
                f.groupId,
            ).length
          : 0,
      resolved:
        this.underwritingStatus >= UnderwritingStatus.RESOLUTION_IN_PROGRESS
          ? this.currentFlags.filter(f => f.status === FlagStatus.RESOLVED && !f.ignore && f.groupId).length
          : 0,
      excluded: this.ignored.length,
    };
  }

  private getInitialSelectedTab(): string {
    if (this.underwritingStatus >= UnderwritingStatus.LETTER_SENT) {
      if (this.underwritingStatus >= UnderwritingStatus.RESOLUTION_IN_PROGRESS) {
        if (this.tabsCount.resolved > 0 && this.tabsCount.toResolve === 0) {
          return 'resolved';
        }
      }
      if (this.underwritingStatus === UnderwritingStatus.LETTER_SENT) {
        if (this.tabsCount.resolved > 0 && this.tabsCount.toResolve === 0) {
          return 'included';
        }
      }
      if (this.tabsCount.toResolve > 0) {
        return 'toResolve';
      }
    }

    //check if we change the status we should not be in to resolve Tab
    if (
      this.selectedTab == 'toResolve' &&
      this.underwritingStatus >= UnderwritingStatus.LETTER_SENT &&
      this.tabsCount.toResolve > 0
    )
      return 'toResolve';

    if (!this.selectedGroup) {
      return this.tabsCount.excluded > 0 ? 'excluded' : '';
    }

    if (this.tabsCount.included > 0) return 'included';
    if (this.tabsCount.excluded > 0) return 'excluded';
    if (this.underwritingStatus >= UnderwritingStatus.LETTER_SENT && this.tabsCount.toResolve > 0) return 'toResolve';
    if (this.underwritingStatus >= UnderwritingStatus.RESOLUTION_IN_PROGRESS && this.tabsCount.resolved > 0)
      return 'resolved';

    return '';
  }

  private checkAndSwitchToResolvedTab(): void {
    if (this.tabsCount.toResolve === 0 && this.tabsCount.resolved > 0 && this.selectedTab === 'toResolve') {
      this.selectedTab = 'resolved';
      this.selectedTabChange(this.selectedTab);
      return;
    }
    if (this.tabsCount.resolved === 0 && this.tabsCount.toResolve > 0) {
      this.selectedTab = 'toResolve';
      this.selectedTabChange(this.selectedTab);
    }
    if (this.underwritingStatus == UnderwritingStatus.LETTER_SENT && this.tabsCount.toResolve === 0) {
      this.selectedTab = 'included';
      this.selectedTabChange(this.selectedTab);
    }
  }

  openCommentsModal() {
    this.commentsModal.openModal();
  }

  openGroup(group) {
    this.flagManagerService.deselectAllFlags();
    this.selectedGroup = group;
  }

  async removeGroup(group) {
    this.dialog.confirm().show({
      header: 'Do you want to permanently delete this group?',
      confirmBtnText: 'Delete',
      confirmed: async () => {
        // Get all flag IDs that were in this group before deletion
        const flagsInGroup = this.allFlags.filter(f => f.groupId === group.id);
        const flagIds = flagsInGroup.map(f => f.id);

        await this.groupService.remove(group.id);
        if (this.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
          this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
        }
        this.toastr.success('Group deleted');
        this.groups = this.groups.filter(g => g.id !== group.id);
        this.selectedGroup = null;

        this.allFlags.forEach(f => {
          if (f.groupId === group.id) {
            f.group = null;
            f.groupId = null;
            f.order = null;
          }
        });

        this.setCurrentFlags();

        // Refresh changelogs for all flags that were in the deleted group
        if (flagIds.length > 0) {
          await this.refreshFlagsAndNotifyUpdates(flagIds);
        }
        this.refreshAnswers.emit();
      },
    });
  }

  async groupSaved(newGroup) {
    this.toastr.success(newGroup.new ? 'You have successfully created a group.' : 'Group saved!');

    const group = this.groups.find(g => g.id === newGroup.id);
    if (group) {
      Object.assign(group, newGroup);

      this.allFlags
        .filter(f => f.groupId === group.id)
        .forEach(f => {
          Object.assign(f.violation, group.violation);
          Object.assign(f.discrepancy, group.discrepancy);
          f.description = group.description;
          f.subtype = group.subtype;
          f.subtypeId = group.subtypeId;
        });
      this.refreshAnswers.emit();
    } else {
      newGroup.checkbox = new UntypedFormControl(false);
      newGroup.count = 0;
    }
    this.editingGroup = null;
    this.editingFlag = null;
    await this.load();

    this.checkUnderwritingStatus();
  }

  checkUnderwritingStatus() {
    const flagsAndGroups = [...this.allFlags, ...this.groups];
    const preparationHasStarted = !!flagsAndGroups.find(x => this.hasInfoDefined(x));
    console.log(
      'preparationHasStarted',
      this.underwritingStatus,
      preparationHasStarted,
      this.inspection.underwritingStatus,
    );
    if (preparationHasStarted && this.inspection.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
      this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
    }
  }

  hasInfoDefined(flagOrGroup: any): boolean {
    if (!flagOrGroup) return false;
    const values = [
      flagOrGroup.description,
      ...Object.values(flagOrGroup.violation || {}),
      ...Object.values(flagOrGroup.discrepancy || {}),
    ];
    return !!values.find(x => !!x);
  }

  onSelectedFlagsChange(selectedFlagIds: number[]) {
    this.selectedFlagIds = selectedFlagIds;
    if (this.selectedTab === 'toResolve' && selectedFlagIds.length > 0) {
      this.shouldShowResolveOptions = true;
    } else {
      this.shouldShowResolveOptions = false;
    }
    this.cdr.detectChanges();
  }

  openEditGroupModal() {
    this.editGroupModal.openModal();
  }

  openFlagGroupTemplateModal(createGroup: boolean = false) {
    this.flagGroupTemplateModal.show(createGroup);
  }

  openFlagGroupEditTemplateModal(createGroup: boolean = false) {
    this.canCreateGroup = false;
    if (!createGroup && this.selectedGroup) {
      // When applying template to existing group, select all flags in that group
      this.selectedFlags = this.allFlags.filter(flag => flag.groupId === this.selectedGroup.id);
      this.selectedFlagIds = this.selectedFlags.map(flag => flag.id);
    }
    this.editGroup = true;
    this.flagGroupTemplateModal.show(createGroup, true); // Pass true for fromEdit parameter
  }

  onCancelFromEditTemplate() {
    this.flagManagerService.deselectAllFlags();
    this.selectedFlagIds = [];
    this.editGroup = false;
    this.canCreateGroup = true;
    this.openEditGroupModal();
  }

  confirmOverridingInfo() {
    return new Promise<boolean>(res => {
      this.dialogService.confirm().show({
        header: 'Confirm Data Replacement',
        message: `The flag-level information will be deleted and replaced by the group-level information, do you want to continue?`,
        confirmed: () => res(true),
        canceled: () => res(false),
      });
    });
  }

  async createGroup(template) {
    this.selectedFlags = this.allFlags.filter(flag => this.selectedFlagIds.includes(flag.id));

    this.flagGroupTemplateModal.open = false;

    if (this.selectedFlags.length > 0) {
      const hasInfoDefined = this.selectedFlags.some(flag => this.hasInfoDefined(flag));
      if (hasInfoDefined && !template.skipConfirmation) {
        const confirmation = await this.confirmOverridingInfo();
        console.log('confirmation', confirmation);
        if (!confirmation) {
          this.flagGroupTemplateModal.loading = false;
          return false;
        }
      }

      const helpPicturesOrNotes = this.selectedFlags.some(
        flag =>
          (flag.helpPictures && flag.helpPictures.length > 0) ||
          (flag.violation?.internalNotes && flag.violation.internalNotes.trim() !== ''),
      );

      if (!hasInfoDefined && helpPicturesOrNotes) {
        const confirmation = await this.confirmOverridingInfo();

        if (!confirmation) {
          this.flagGroupTemplateModal.show();
          this.flagGroupTemplateModal.preview = false;
          this.flagGroupTemplateModal.loading = false;
          return false;
        }
      }
    }

    try {
      this.loading = true;
      const newGroup = await this.flagGroupService.createWithFlags(
        template,
        this.inspection.id,
        this.selectedFlags.map(flag => flag.id), // Will be empty array if no flags selected
      );
      console.log(template);
      if (this.editGroup) {
        this.toastr.success('Group Updated');
        this.flagManagerService.deselectAllFlags();
        this.editGroup = false;
        this.selectedGroup = newGroup;
      } else {
        this.toastr.success('You have successfully created a group.');
      }

      if (template.subtype && this._underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
        this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
      }
      if (!template.subtype) {
        this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
      }

      this.flagGroupTemplateModal.loading = false;
      await this.load();
      this.refreshAnswers.emit();
      this.flagGroupTemplateModal.cancel();
      this.loading = false;
      return newGroup;
    } catch (error) {
      this.toastr.error('Failed to create group');
      console.error(error);
      return false;
    }
  }

  async editGroupByTemplate(group) {
    this.loading = true;
    group.name = this.flagGroupService.getGroupName(
      this.groups.filter(g => g.id !== group.id),
      group.name,
    );
    this.selectedGroup = await this.flagGroupService.edit(group.id, group);
    await this.load();
    this.loading = false;
    this.canCreateGroup = true;
    this.flagGroupTemplateModal.cancel();
  }

  setUnderwritingStatusService(status: UnderwritingStatus) {
    if (this._underwritingStatus != status) {
      this.flagManagerService.updateUnderwritingStatus(this.inspection.id, status);
    }
  }

  isReadonly() {
    if (this.underwritingStatus >= UnderwritingStatus.LETTER_SENT) return true;
    else return false;
  }

  selectedTabChange(selectedTab: string) {
    this.selectedTab = selectedTab;
  }

  resolveBarTabChange(resolveBarSelectedTab: string) {
    this.resolveBarSelectedTab = resolveBarSelectedTab;
  }

  async changeFlagStatusBulk(status: FlagStatus) {
    this.dialog.confirm().show({
      message: `Do you want to change the status of all selected flags?`,
      confirmed: async () => {
        this.loading = true;
        try {
          await this.flagService.changeFlagsStatus({ flagSelectedIds: this.selectedFlagIds, flagStatus: status });
          const tab = this.selectedTab;
          await this.refreshFlagsAndNotifyUpdates(this.selectedFlagIds, { reload: true });
          this.selectedTab = tab;
          if (this.tabsCount.toResolve === 0 && this.tabsCount.resolved > 0) {
            this.selectedTab = 'resolved';
            this.selectedTabChange(this.selectedTab);
            this.toastr.success('You have successfully resolved flags and moved to the Resolved Flags tab.');
          } else if (this.tabsCount.resolved === 0 && this.tabsCount.toResolve > 0) {
            this.selectedTab = 'toResolve';
            this.selectedTabChange(this.selectedTab);
            this.toastr.success('Flags status changed');
          } else {
            this.toastr.success('Flags status changed');
          }

          if (
            this.underwritingStatus != UnderwritingStatus.RESOLUTION_IN_PROGRESS &&
            this.underwritingStatus != UnderwritingStatus.CLOSED
          ) {
            this.flagManagerService.updateUnderwritingStatus(
              this.inspection.id,
              UnderwritingStatus.RESOLUTION_IN_PROGRESS,
            );
          }
        } catch (error) {
          console.error('Error changing flag status:', error);
          this.toastr.error('Failed to change flag status');
        } finally {
          this.loading = false;
        }
      },
    });
  }

  async removeSelected(selectedGroups) {
    let headerMsg = '';
    if (selectedGroups.length == 1) headerMsg = 'Do you want to permanently delete this group?';
    else headerMsg = 'Do you want to permanently delete these groups?';
    this.dialog.confirm().show({
      header: headerMsg,
      confirmBtnText: 'Delete',
      confirmed: async () => {
        this.loading = true;
        try {
          const promises = [];
          const ids = selectedGroups.map(g => g.id);

          // Get all flag IDs that were in the deleted groups before deletion
          const flagsInGroups = this.allFlags.filter(f => ids.includes(f.groupId));
          const flagIds = flagsInGroups.map(f => f.id);

          ids.forEach(id => promises.push(this.groupService.remove(id)));
          await Promise.all(promises);

          this.toastr.success('Groups removed');
          this.groups = this.groups.filter(g => !ids.includes(g.id));
          this.selectedGroup = null;

          this.allFlags.forEach(f => {
            if (ids.includes(f.groupId)) {
              f.group = null;
              f.groupId = null;
              f.order = null;
            }
          });

          this.setCurrentFlags();

          // Refresh changelogs for all flags that were in the deleted groups
          if (flagIds.length > 0) {
            await this.refreshFlagsAndNotifyUpdates(flagIds);
          }
        } catch (error) {
          console.error('Error removing groups:', error);
          this.toastr.error('Failed to remove groups');
        } finally {
          this.loading = false;
        }
      },
    });
  }

  async mergeGroups(ev: { targetGroup: FlagGroup; selectedGroups: any[] }) {
    const { targetGroup, selectedGroups } = ev;

    this.loading = true;

    try {
      const groupIds = selectedGroups.filter(g => g.id !== targetGroup.id).map(g => g.id);
      const flags = this.allFlags.filter(f => groupIds.includes(f.groupId));

      flags.forEach(flag => {
        flag.groupId = targetGroup.id;
        flag.group = targetGroup;
        Object.assign(flag.violation, targetGroup.violation);
        Object.assign(flag.discrepancy, targetGroup.discrepancy);
        flag.subtype = targetGroup.subtype;
        flag.subtypeId = targetGroup.subtypeId;
        flag.description = targetGroup.description;

        // update "global" object
        flag.flag.groupId = targetGroup.id;
        flag.flag.group = targetGroup;
        Object.assign(flag.flag.violation, targetGroup.violation);
        Object.assign(flag.flag.discrepancy, targetGroup.discrepancy);
        flag.flag.subtype = targetGroup.subtype;
        flag.flag.subtypeId = targetGroup.subtypeId;
        flag.flag.description = targetGroup.description;
      });

      flags.forEach(flag => {
        if (!targetGroup.flags.find(f => f.id === flag.id)) {
          targetGroup.flags.push(flag);
        }
      });

      await Promise.all(flags.map(f => this.flagService.addToGroup(targetGroup.id, f.id)));

      const idsToRemove = selectedGroups.filter(g => g.id !== targetGroup.id).map(g => g.id);
      this.groups = this.groups.filter(g => !idsToRemove.includes(g.id));
      this.selectedGroup = targetGroup;

      await Promise.all(idsToRemove.map(id => this.groupService.remove(id)));
      if (this.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
        this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
      }
      this.toastr.success(`You have successfully merged ${selectedGroups.length} groups.`);
      this.groups.forEach(g => g.checkbox.setValue(false));

      await this.refreshFlagsAndNotifyUpdates(null, {
        reload: true,
        refreshAllInGroup: true,
      });
    } catch (error) {
      console.error('Error merging groups:', error);
      this.toastr.error('Failed to merge groups');
    } finally {
      this.loading = false;
    }
  }

  async excludeFlags() {
    this.selectedFlags = this.allFlags.filter(flag => this.selectedFlagIds.includes(flag.id));
    this.dialog.confirm().show({
      header: 'Do you want to exclude all selected flags?',
      confirmBtnText: 'Exclude',
      confirmed: async () => {
        this.loading = true;
        try {
          const selectedIds = new Set(this.selectedFlagIds);
          await Promise.all(
            this.selectedFlags.map(flag => {
              flag.ignore = true;
              flag.flag.ignore = true;
              const { checkbox, ...cleanFlag } = flag;
              return this.flagService.edit(flag.id, cleanFlag);
            }),
          );

          // Update flag state
          this.updateFlagIgnoreState(selectedIds, true);

          this.setCurrentFlags();

          await this.refreshFlagsAndNotifyUpdates(Array.from(selectedIds));
          this.refreshAnswers.emit();

          let toastMsg = '';
          if (this.allCurrentAreExcluded())
            toastMsg = 'All flags excluded; issue moved to Not on Rec Letter and flags moved to Excluded tab.';
          else toastMsg = 'Selected flags excluded and moved to the Excluded tab.';
          this.toastr.success(toastMsg);
          if (this.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
            this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
          }
        } catch (error) {
          console.error('Error excluding flags:', error);
          this.toastr.error('Failed to exclude flags');
        } finally {
          this.loading = false;
        }
      },
    });
  }

  async includeFlags() {
    this.selectedFlags = this.allFlags.filter(flag => this.selectedFlagIds.includes(flag.id));
    const isExcluded = this.allCurrentAreExcluded();
    this.dialog.confirm().show({
      header: `Do you want to include all selected flags?`,
      confirmBtnText: 'Include',
      confirmed: async () => {
        this.loading = true;
        try {
          const selectedIds = new Set(this.selectedFlagIds);
          await Promise.all(
            this.selectedFlags.map(flag => {
              flag.ignore = false;
              flag.flag.ignore = false;
              const { checkbox, ...cleanFlag } = flag;
              return this.flagService.edit(flag.id, cleanFlag);
            }),
          );

          // Update flag state
          this.updateFlagIgnoreState(selectedIds, false);

          this.setCurrentFlags();

          await this.refreshFlagsAndNotifyUpdates(Array.from(selectedIds));
          this.refreshAnswers.emit();

          let toastMsg = '';
          if (isExcluded) {
            toastMsg = 'Flags included; issue moved to Included on Rec Letter and flags moved to Included tab.';
          } else {
            toastMsg = 'Selected flags included and moved to the Included tab.';
          }
          if (this.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
            this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
          }
          this.toastr.success(toastMsg);
        } catch (error) {
          console.error('Error including flags:', error);
          this.toastr.error('Failed to include flags');
        } finally {
          this.loading = false;
        }
      },
    });
  }

  private updateFlagIgnoreState(selectedIds: Set<number>, ignore: boolean): void {
    // Capture pre-change state for the selected group based on current flags
    this.allFlags.forEach(f => {
      if (selectedIds.has(f.id)) {
        f.ignore = ignore;
        f.checkbox?.setValue?.(false);
      }
    });

    // Update currentFlags
    this.currentFlags.forEach(f => {
      if (selectedIds.has(f.id)) {
        f.ignore = ignore;
        f.checkbox?.setValue?.(false);
      }
    });

    // Update groups
    this.groups?.forEach(g => {
      g?.flags?.forEach(fl => {
        if (selectedIds.has(fl.id)) {
          fl.ignore = ignore;
          fl.checkbox?.setValue?.(false);
        }
      });
    });

    this.groups = [...this.groups];
  }

  allSelectedAreIncluded() {
    return this.currentFlags?.every(f => !f.ignore) ?? false;
  }

  allCurrentAreExcluded() {
    return this.currentFlags?.every(f => f.ignore) ?? false;
  }

  ungroupFlags() {
    this.selectedFlags = this.allFlags.filter(flag => this.selectedFlagIds.includes(flag.id));
    const nSelected = this.selectedFlags.length;
    const nTotalFlags = this.currentFlags.length;

    if (nSelected === nTotalFlags) {
      return this.unlinkAllFromSelectedGroup();
    }

    this.dialog.confirm().show({
      header: `Do you want to unlink all selected flags?`,
      confirmed: async () => {
        this.loading = true;
        try {
          await Promise.all(
            this.selectedFlags.map(f => {
              f.group = null;
              f.groupId = null;
              f.order = null;
              return this.flagService.addToGroup(null, f.id);
            }),
          );
          this.removeFlagsFromGroups(this.selectedFlags);
          if (this.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
            this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
          }
          this.toastr.success('Flags saved');
          this.setCurrentFlags();
          this.refreshAnswers.emit();

          const flagIds = this.selectedFlags.map(f => f.id);
          await this.refreshFlagsAndNotifyUpdates(flagIds);
          this.flagManagerService.deselectAllFlags();
        } catch (error) {
          console.error('Error ungrouping flags:', error);
          this.toastr.error('Failed to ungroup flags');
        } finally {
          this.loading = false;
        }
      },
    });
  }

  async unlinkAllFromSelectedGroup() {
    const option = await this.askUnlinkAllOption();
    if (option === UnlinkAllOption.CANCEL) return;

    this.loading = true;
    try {
      if (option === UnlinkAllOption.CLEAR) {
        const group = this.selectedGroup;
        group.violation = {} as any;
        group.discrepancy = {} as any;
        group.description = null;

        await this.groupService.edit(group.id, group);
        this.toastr.success('Group saved');
      }

      await Promise.all(
        this.currentFlags.map(f => {
          f.group = null;
          f.groupId = null;
          f.order = null;
          return this.flagService.addToGroup(null, f.id);
        }),
      );

      this.removeFlagsFromGroups(this.currentFlags);

      this.toastr.success('Flags saved');

      // Refresh flags with latest changes and notify updates
      const flagIds = this.currentFlags.map(f => f.id);
      await this.refreshFlagsAndNotifyUpdates(flagIds, { reload: true });
      this.refreshAnswers.emit();
    } catch (error) {
      console.error('Error unlinking all flags:', error);
      this.toastr.error('Failed to unlink all flags');
    } finally {
      this.loading = false;
    }
  }

  removeFlagsFromGroups(flags: any[]): void {
    const removedFlags = flags.map(f => f.id);
    const groups = [];
    for (const group of this.groups) {
      if (group.flags.length) {
        group.flags = group.flags.filter(f => !removedFlags.includes(f.id));
      }
      group.count = group.flags.length;
      groups.push(group);
    }
    this.groups = groups;
  }

  askUnlinkAllOption() {
    return this.unlinkConfirm.show();
  }

  isCompleted(flagOrGroup: Flag | FlagGroup) {
    // Uncategorized
    if (!flagOrGroup) {
      return (
        this.includedGroupsFlags && !this.includedGroupsFlags.filter(f => !f.groupId).find(f => !this.isCompleted(f))
      );
    }

    return this.flagService.isCompleted(flagOrGroup);
  }

  nCompletedFlags() {
    if (this.includedGroupsFlags) {
      return this.includedGroupsFlags.reduce((total, flag) => (this.isCompleted(flag) ? total + 1 : total), 0);
    }
  }

  flagRefresh(status: number) {
    if (status !== undefined && status !== null) {
      this.setUnderwritingStatusService(status);
    }

    this.load();
  }

  async onFlagDropped(ev: { flagId: number; targetGroupId: number | null; revert: () => void }) {
    try {
      const flag = this.allFlags?.find(f => f.id === ev.flagId);
      const targetGroup = ev.targetGroupId ? this.groups?.find(g => g.id === ev.targetGroupId) : null;

      // Check if confirmation is needed (only show popup once)
      let needsConfirmation = false;

      if (targetGroup && this.hasInfoDefined(targetGroup)) {
        needsConfirmation = true;
      }

      if (!needsConfirmation && targetGroup && flag) {
        const flagSubtypeId = flag.subtypeId || flag.subtype?.id;
        const groupSubtypeId = targetGroup.subtypeId || targetGroup.subtype?.id;

        if (flagSubtypeId && groupSubtypeId && flagSubtypeId !== groupSubtypeId) {
          needsConfirmation = true;
        }
      }

      if (needsConfirmation) {
        const confirmed = await this.confirmOverridingInfo();
        if (!confirmed) {
          ev.revert();
          // Reload to resync Sortable.js state with the DOM after revert
          await this.load();
          return;
        }
      }

      this.loading = true;
      await this.flagService.addToGroup(ev.targetGroupId, ev.flagId);

      // Update local flag object
      if (flag) {
        flag.groupId = ev.targetGroupId;
        flag.group = ev.targetGroupId ? this.groups?.find(g => g.id === ev.targetGroupId) : null;
        // Remove order when moving to uncategorized
        if (ev.targetGroupId === null) {
          flag.order = null;
        }
      }

      // Update current flags view
      this.setCurrentFlags();

      // Refresh flag with latest changes and notify updates
      await this.refreshFlagsAndNotifyUpdates([ev.flagId], { reload: true });
      this.toastr.success('Flag moved');
    } catch (error) {
      console.error('Error moving flag via drag-and-drop:', error);
      this.toastr.error('Failed to move flag');
      // On error, also revert the UI and reload to resync Sortable.js state
      ev.revert();
      await this.load();
    } finally {
      this.loading = false;
    }
  }

  showReportButton() {
    if (this.grouploading) return false;
    return this.nCompletedFlags() == (this.includedGroupsFlags && this.includedGroupsFlags.length);
  }

  async mergeSelectedFlagsIntoGroup(targetGroup: FlagGroup) {
    this.selectedFlags = this.allFlags.filter(flag => this.selectedFlagIds.includes(flag.id));
    if (!this.selectedFlags.length) {
      return;
    }

    this.loading = true;
    try {
      if (!targetGroup.subtype) {
        await this.loadGroups();
        const refreshed = this.groups.find(g => g.id === targetGroup.id);
        if (refreshed) targetGroup = refreshed;
      }

      this.selectedFlags.forEach(flag => {
        flag.groupId = targetGroup.id;
        flag.group = targetGroup;

        Object.assign(flag.violation, targetGroup.violation);
        Object.assign(flag.discrepancy, targetGroup.discrepancy);
        flag.subtype = targetGroup.subtype;
        flag.subtypeId = targetGroup.subtypeId;
        flag.description = targetGroup.description;

        flag.flag.groupId = targetGroup.id;
        flag.flag.group = targetGroup;
        Object.assign(flag.flag.violation, targetGroup.violation);
        Object.assign(flag.flag.discrepancy, targetGroup.discrepancy);
        flag.flag.subtype = targetGroup.subtype;
        flag.flag.subtypeId = targetGroup.subtypeId;
        flag.flag.description = targetGroup.description;
      });

      await Promise.all(this.selectedFlags.map(f => this.flagService.addToGroup(targetGroup.id, f.id)));

      this.selectedFlags.forEach(f => {
        if (!targetGroup.flags.find(x => x.id === f.id)) {
          targetGroup.flags.push(f);
        }
      });

      this.flagManagerService.deselectAllFlags();
      this.refreshAnswers.emit();

      await this.refreshFlagsAndNotifyUpdates(null, {
        reload: true,
        refreshAllInGroup: true,
      });
      if (this.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
        this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
      }
      const n = this.selectedFlags.length;
      if (n === 1) {
        this.toastr.success('You have successfully moved a flag into another group.');
      } else {
        this.toastr.success(`You have successfully moved ${n} flags into another group.`);
      }
    } catch (error) {
      console.error('Error merging flags into group:', error);
      this.toastr.error('Failed to merge flags into group');
    } finally {
      this.loading = false;
    }
  }

  async CreateGroupFromSidebar(name: string) {
    this.loading = true;
    const group = await this.flagGroupService.create(this.inspection.id, { name: name }, false);
    if (this.inspection.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
      this.setUnderwritingStatusService(UnderwritingStatus.PREPARATION_IN_PROGRESS);
    }
    await this.load();
    this.toastr.success('You have successfully created a group.');
    this.loading = false;
    return group;
  }
  catch(error) {
    this.toastr.error('Failed to create group');
    console.error(error);
    return false;
  }

  openSidebarGroupModal() {
    this.sidebarGroupNameModal.show([], this.groups);
  }

  updateFlagsWithPicture(flags: any[], picture: any) {
    if (!flags) return;

    const flagsWithSamePicture = flags.filter(flag => flag.picture?.id === picture.id);

    flagsWithSamePicture.forEach(flag => {
      if (flag) {
        flag._needsImageRefresh = true;
        flag.picture = { ...flag.picture, ...picture };
        flag.picture.updatedAt = picture.updatedAt || Date.now();
        const timestamp = flag.picture.updatedAt;
        flag.imageUrl = `${env.APIUrl}/image/embedded/${picture.id}?t=${timestamp}`;
      }
    });
  }

  hasExtendedDeadline(flag: any): boolean {
    if (flag.subtype?.type?.name !== 'Violation' || !flag?.violation?.deadline || !this.inspection) {
      return false;
    }

    const flagDeadlineDate = flag.violation.deadline.split('T')[0];

    const actionRequiredDate = this.inspection.violationActionRequiredDeadline
      ? new Date(this.inspection.violationActionRequiredDeadline).toISOString().split('T')[0]
      : null;
    const recommendedDate = this.inspection.violationRecommendedDeadline
      ? new Date(this.inspection.violationRecommendedDeadline).toISOString().split('T')[0]
      : null;

    const isDifferentFromAction = !actionRequiredDate || flagDeadlineDate !== actionRequiredDate;
    const isDifferentFromRecommended = !recommendedDate || flagDeadlineDate !== recommendedDate;

    return isDifferentFromAction && isDifferentFromRecommended;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
